import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../components/AnalyticsTracker';
import { User, Mail, Lock, Calendar, Phone, CheckCircle, AlertCircle, Users, Shield, School, Eye, EyeOff } from 'lucide-react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import DateOfBirthModal from '../components/modals/DateOfBirthModal';
import { useLanguage } from '../contexts/LanguageContext';
import MayaProfileChat from '../components/MayaProfileChat';
import MayaErrorBoundary from '../components/MayaErrorBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, socialLogin, completeSocialSignup } = useAuth();
  const [step, setStep] = useState(1);
  const honeypotRef = useRef('');
  const formLoadedAtRef = useRef(Date.now());
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    phoneCountry: '+1',
    smsConsent: false,
    month: '',
    day: '',
    year: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    relationship: 'parent',
    parentConsent: false,
    rememberMe: false,
    favoriteColor: '',
    favoriteAnimal: '',
    favoritePlace: '',
    favoriteActor: '',
    favoriteSport: '',
    oceanAnswers: {}
  });
  const [age, setAge] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [error, setError] = useState('');

  // QA report M-03/M-06: errors used to render at the top of a long form,
  // invisible from the Continue / Create Account buttons. Bring them into view.
  useEffect(() => {
    if (!error) return;
    const el = document.getElementById('register-error-box');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);
  // Persist a friend-referral code (?ref=CODE) so it survives the
  // multi-step signup even if the query string is lost along the way.
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) localStorage.setItem('hibff_ref_code', ref);
    } catch (e) { /* */ }
  }, []);
  const [loading, setLoading] = useState(false);
  const [showDOBModal, setShowDOBModal] = useState(false);
  const [pendingSocialUser, setPendingSocialUser] = useState(null);
  const [schoolCode, setSchoolCode] = useState('');
  const [schoolValidation, setSchoolValidation] = useState({ status: 'idle', name: '', error: '' });
  const [validatingCode, setValidatingCode] = useState(false);

  // Persona selector — restores last choice or defaults to 'teen'.
  // URL query param `?persona=adult` from the homepage adult-track CTAs
  // takes precedence over the persisted localStorage value.
  const [persona, setPersona] = useState(() => {
    // Adult track (Oct 2026): ONLY the /adult route or ?persona=adult open the
    // 20+ signup. Plain /register always defaults to teen (never sticky —
    // a shared family device must not show the adult form to a teen).
    try {
      if (window.location.pathname === '/adult') return 'adult';
      if (new URLSearchParams(window.location.search).get('persona') === 'adult') return 'adult';
    } catch (e) { /* */ }
    return 'teen';
  });
  useEffect(() => {
    try { localStorage.setItem('hibff_signup_persona', persona); } catch (e) { /* */ }
  }, [persona]);
  const handlePersonaPick = (next) => {
    if (next === 'parent') {
      navigate('/parent-register');
      return;
    }
    try { localStorage.setItem('hibff_signup_persona', next); } catch (e) { /* */ }
    setPersona(next);
    setError('');
    if (next === 'adult' && window.location.pathname !== '/adult') navigate('/adult', { replace: true });
    if (next === 'teen' && window.location.pathname !== '/register') navigate('/register', { replace: true });
  };
  const PERSONA_THEME = {
    teen:  { grad: 'from-indigo-600 to-violet-600', glow: 'shadow-indigo-200', sub: 'Start your social-skills practice space — your AvA is one click away.' },
    adult: { grad: 'from-amber-500 to-orange-600',  glow: 'shadow-amber-200',  sub: 'Start your confidence journey — practice the conversations that matter.' },
  };
  const theme = PERSONA_THEME[persona] || PERSONA_THEME.teen;

  // Check if coming from social login with parent consent needed
  const [googleWaiting, setGoogleWaiting] = useState(false);
  useEffect(() => {
    if (location.state?.socialUser && location.state?.step === 2) {
      const socialUser = location.state.socialUser;
      setFormData(prev => ({
        ...prev,
        fullName: socialUser.name,
        email: socialUser.email
      }));
      setAge(socialUser.age);
      setStep(3);
    }
  }, [location.state]);

  const calculateAge = () => {
    if (formData.year && formData.month && formData.day) {
      const birthDate = new Date(formData.year, formData.month - 1, formData.day);
      const today = new Date();
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }
      setAge(calculatedAge);
      return calculatedAge;
    }
    return null;
  };

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    setPasswordStrength(strength);
  };

  // Validate school enrollment code
  const validateSchoolCode = async (code) => {
    const trimmed = code.trim();
    if (!trimmed) {
      setSchoolValidation({ status: 'idle', name: '', error: '' });
      return;
    }
    if (trimmed.length < 4) return; // Don't validate very short strings
    setValidatingCode(true);
    try {
      const res = await fetch(`${API_URL}/api/school/access-codes/validate/${encodeURIComponent(trimmed)}`);
      if (res.ok) {
        const data = await res.json();
        setSchoolValidation({ status: 'valid', name: data.school_name, error: '' });
      } else if (res.status === 404) {
        setSchoolValidation({ status: 'invalid', name: '', error: "We don't recognise that code. Please check and try again." });
      } else {
        const data = await res.json().catch(() => ({}));
        setSchoolValidation({ status: 'invalid', name: '', error: data.detail || "This code isn't valid right now." });
      }
    } catch {
      setSchoolValidation({ status: 'invalid', name: '', error: 'Unable to check code. Please try again.' });
    }
    setValidatingCode(false);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    checkPasswordStrength(newPassword);
  };

  const handleContinue = () => {
    setError('');
    // Field-by-field pre-checks (QA report M-03: "Continue gives no feedback")
    if (!formData.fullName.trim()) { setError('Please enter your full name.'); return; }
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRe.test((formData.email || '').trim())) { setError('Please enter a valid email address.'); return; }
    if ((formData.password || '').length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match.'); return; }
    const userAge = calculateAge();
    if (userAge === null) { setError('Please enter your date of birth.'); return; }
    if (userAge !== null) {
      if (userAge < 13) {
        setError('You must be at least 13 years old to use HiBFF. For more information, visit our COPPA Compliance page.');
        return;
      }
      if (persona === 'adult') {
        if (userAge < 20) {
          setError('Adult accounts are for ages 20 and over — switch to the Teen tab (13\u201319) instead.');
          return;
        }
      } else if (userAge > 19) {
        setError('HIBFF_AGE_BLOCKED');
        return;
      }
      // Mobile number is mandatory — used for 2FA and account recovery.
      // Validated with libphonenumber-js so no invalid number ever reaches Twilio.
      const dialCode = (formData.phoneCountry || '').split('-')[0];
      const nationalDigits = (formData.phone || '').replace(/\D/g, '').replace(/^0/, '');
      if (!nationalDigits || !isValidPhoneNumber(`${dialCode}${nationalDigits}`)) {
        setError('Please enter a valid mobile number for the selected country code.');
        return;
      }
      if (!formData.smsConsent) {
        setError('Please tick the SMS consent box — we need it to send you account and security texts.');
        return;
      }
      // Teens: parent/guardian name, email and mobile are MANDATORY before
      // the form can advance. We need a verified adult on file before any
      // teen account can be created — they handle billing + COPPA consent.
      if (persona === 'teen') {
        if (!formData.parentName.trim()) {
          setError("Please enter your parent or guardian's full name.");
          return;
        }
        const parentEmailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
        if (!parentEmailRe.test(formData.parentEmail.trim())) {
          setError("Please enter a valid email address for your parent or guardian.");
          return;
        }
        const parentPhoneDigits = (formData.parentPhone || '').replace(/\D/g, '').replace(/^0/, '');
        if (!parentPhoneDigits || !isValidPhoneNumber(`${dialCode}${parentPhoneDigits}`)) {
          setError("Please enter a valid mobile number for your parent or guardian (same country code).");
          return;
        }
      }
      setStep(2);
    }
  };

  const profilingOptions = {
    colors: ['Red', 'Blue', 'Green', 'Purple', 'Yellow', 'Orange', 'Pink', 'Black', 'White', 'Gold', 'Teal', 'Silver'],
    animals: ['Dog', 'Cat', 'Horse', 'Dolphin', 'Eagle', 'Lion', 'Tiger', 'Panda', 'Butterfly', 'Wolf', 'Rabbit', 'Owl'],
    places: ['Beach', 'Mountains', 'City Lights', 'Forest', 'Island', 'Desert', 'Lake', 'Countryside', 'Theme Park', 'Outer Space'],
    sports: ['Football', 'Basketball', 'Soccer', 'Tennis', 'Swimming', 'Gymnastics', 'Running', 'Volleyball', 'Baseball', 'Dance', 'Martial Arts', 'Skateboarding']
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (persona === 'teen' && age >= 13 && age <= 19 && !formData.parentConsent) {
      setError('Parent consent is required for all teen accounts (ages 13\u201319)');
      return;
    }

    // Safety net — all 12 preference answers + 10 OCEAN answers MUST be
    // present before the form can submit. The Maya chat UI already
    // forces a full walk-through, but a refresh-mid-flow or a tampered
    // client state should still be rejected here.
    const REQUIRED_PREF_FIELDS = ['firstName', 'age', 'pronouns', 'favoriteColor', 'mainGoal', 'preferredVibe', 'topHobby', 'musicGenre', 'favoriteMovie', 'spiritAnimal', 'happyPlace', 'superpower'];
    const missingPrefs = REQUIRED_PREF_FIELDS.filter(f => !formData[f] || String(formData[f]).trim() === '');
    const oceanCount = Object.keys(formData.oceanAnswers || {}).length;
    if (missingPrefs.length > 0 || oceanCount < 10) {
      setError(`Please finish all 22 questions in the AvA chat before continuing. ${missingPrefs.length} preferences and ${10 - oceanCount} personality answers are still missing.`);
      setStep(2);
      return;
    }

    setLoading(true);

    try {
      // Capture signup_intent_plan from ?plan=… query param (set by the
      // Homepage banner / Welcome Pack badge / Pricing page) so we can
      // attribute conversions back to the entry point. Free is the default
      // tier on every new account — this is metadata only.
      const urlParams = new URLSearchParams(window.location.search);
      const intentPlan = urlParams.get('plan');
      const utmSource = urlParams.get('utm_source') || localStorage.getItem('hibff_utm_source');
      const refCode = urlParams.get('ref') || localStorage.getItem('hibff_ref_code');

      // Map Maya chat answers → backend UserCreate fields. The 12 chat
      // preference questions all persist now via the new `profile` keys
      // on the backend so the AvA prompt, the first-message generator
      // and the meme prompt builder can all read them by name.
      // Combine country dial code + national digits into a single E.164-style
      // string so the backend's 2FA / SMS path receives a fully-qualified
      // number. `phoneCountry` may carry a regional suffix like "+1-ca" — we
      // strip anything after the dash before joining.
      const phoneDial = (formData.phoneCountry || '').split('-')[0];
      // Strip a single leading 0 (UK-style national format) so E.164 joins cleanly.
      const phoneDigits = (formData.phone || '').replace(/\D/g, '').replace(/^0/, '');
      const fullPhone = phoneDigits ? `${phoneDial}${phoneDigits}` : null;

      const userData = {
        name: formData.fullName,
        email: formData.email,
        password: formData.password,
        dob: `${formData.year}-${String(formData.month).padStart(2, '0')}-${String(formData.day).padStart(2, '0')}`,
        age: age,
        phone: fullPhone,
        schoolCode: schoolCode.trim() || null,
        // Legacy profile fields (kept for back-compat with older flows)
        favorite_color: formData.favoriteColor || null,
        favorite_animal: formData.spiritAnimal || formData.favoriteAnimal || null,
        favorite_place: formData.happyPlace || formData.favoritePlace || null,
        favorite_actor: formData.favoriteActor || null,
        favorite_sport: formData.favoriteSport || null,
        // ── Maya chat (12-question block) — verbatim preferences ──
        pronouns: formData.pronouns || null,
        main_goal: formData.mainGoal || null,
        preferred_vibe: formData.preferredVibe || null,
        top_hobby: formData.topHobby || null,
        music_genre: formData.musicGenre || null,
        favorite_movie: formData.favoriteMovie || null,
        spirit_animal: formData.spiritAnimal || null,
        happy_place: formData.happyPlace || null,
        superpower: formData.superpower || null,
        signup_intent_plan: intentPlan || null,
        signup_utm_source: utmSource || null,
        referral_code: refCode || null,
        website: honeypotRef.current || null,
        form_ms: Date.now() - formLoadedAtRef.current,
        sms_consent: formData.smsConsent === true,
        persona: persona || 'teen',
      };

      // Add parent info — required for every teen account (13-19)
      if (persona === 'teen' && age >= 13 && age <= 19) {
        userData.parentName = formData.parentName;
        userData.parentEmail = formData.parentEmail;
        // Parent mobile shares the teen's country code — join dial + national
        // digits (stripping a leading 0) so Twilio receives a valid E.164
        // number. Previously the raw national number was sent and every
        // parent SMS failed with Twilio 21211 (invalid number).
        const parentDigits = (formData.parentPhone || '').replace(/\D/g, '').replace(/^0/, '');
        userData.parentPhone = parentDigits ? `${phoneDial}${parentDigits}` : null;
        userData.relationship = formData.relationship;
        userData.parentSetupComplete = false;
      }

      await register(userData, formData.rememberMe);

      // Fire-and-forget OCEAN submission — Maya already captured 10 Likert
      // answers during step 2 of the chat. The auth token is now set by
      // AuthContext (apiClient.setToken) so the call is authorised. We
      // never block registration success on the OCEAN POST: failures are
      // logged and the user can complete the assessment from the post-
      // login MandatoryRegistrationGate instead.
      if (formData.oceanAnswers && Object.keys(formData.oceanAnswers).length === 10) {
        try {
          const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
          const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
          await fetch(`${apiUrl}/api/ocean/submit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ answers: formData.oceanAnswers }),
          });
        } catch (_e) { /* non-fatal */ }
      }

      // Funnel analytics — geo-stamped registration event
      try { trackEvent('registration', { persona: persona || 'teen' }); } catch (_e) { /* noop */ }

      // Navigate to email verification
      navigate('/verify-email', {
        state: {
          email: formData.email,
          password: formData.password,
          rememberMe: formData.rememberMe
        }
      });
    } catch (err) {
      if (err.message === 'EMAIL_EXISTS') {
        setError('An account with this email already exists. Please login instead.');
      } else if (err.message?.includes('enrollment code') || err.message?.includes('school')) {
        setError(err.message);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError('');
    setLoading(true);

    try {
      // Direct Google OAuth — no Emergent intermediate screen. Native app:
      // device-link handshake (flow finishes in the system browser, we poll).
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const { signInWithGoogle, routeForUser, isNativeApp } = await import('../utils/googleAuth');
      if (isNativeApp()) setGoogleWaiting(true);
      const data = await signInWithGoogle();
      if (data) {
        localStorage.setItem('hibff_auth_token', data.access_token);
        sessionStorage.removeItem('hibff_auth_token');
        localStorage.setItem('hibff_user_cache', JSON.stringify(data.user));
        navigate(routeForUser(data.user), { replace: true });
      }
    } catch (err) {
      if (err.message !== 'GOOGLE_SIGNIN_CANCELLED') {
        setError(err.message || 'Google sign-up failed. Please try again.');
      }
      setLoading(false);
    } finally {
      setGoogleWaiting(false);
    }
  };

  const cancelGoogleWait = async () => {
    const { cancelGoogleSignIn } = await import('../utils/googleAuth');
    cancelGoogleSignIn();
  };

  const handleFacebookSignup = async () => {
    setError('');
    setLoading(true);

    try {
      const mockFBUser = {
        name: 'Michael Chen',
        email: 'michael.chen@facebook.com',
        profilePicture: 'https://api.dicebear.com/7.x/avataaars/svg?seed=MichaelFB&backgroundColor=ffd5dc',
        authProvider: 'facebook'
      };

      const result = await socialLogin('Facebook', mockFBUser, formData.rememberMe);

      if (result.type === 'LOGIN_SUCCESS') {
        navigate('/dashboard');
      } else if (result.type === 'NEEDS_DOB') {
        setPendingSocialUser(result.userData);
        setShowDOBModal(true);
      }
    } catch (err) {
      setError('Facebook sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDOBSubmit = async ({ dob, age }) => {
    setShowDOBModal(false);
    setLoading(true);

    try {
      if (age < 13) {
        setError('You must be at least 13 years old to use HiBFF. For more information, visit our COPPA Compliance page.');
        return;
      }
      if (age > 19 && persona !== 'adult') {
        setError('HIBFF_AGE_BLOCKED');
        return;
      }

      const result = await completeSocialSignup(pendingSocialUser, dob, age, formData.rememberMe);

      if (result.type === 'SIGNUP_SUCCESS') {
        navigate('/dashboard');
      } else if (result.type === 'NEEDS_PARENT_CONSENT') {
        // Show parent consent form - set up the data and go to step 3
        setFormData(prev => ({
          ...prev,
          fullName: result.userData.name,
          email: result.userData.email
        }));
        setAge(result.userData.age);
        setStep(3);
      }
    } catch (err) {
      setError('Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString({ en: 'en', es: 'es', fr: 'fr', de: 'de', tl: 'fil', pt: 'pt-BR', ar: 'ar' }[lang] || 'en', { month: 'long' }) }));
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

  // Live age from the DOB selectors (null until all three are picked) —
  // drives the 18-19 parent/billing explainer in the parent section.
  const declaredAge = (formData.year && formData.month && formData.day)
    ? (() => {
        const b = new Date(formData.year, formData.month - 1, formData.day);
        const td = new Date();
        let a = td.getFullYear() - b.getFullYear();
        const md = td.getMonth() - b.getMonth();
        if (md < 0 || (md === 0 && td.getDate() < b.getDate())) a -= 1;
        return a;
      })()
    : null;

  const getStrengthColor = () => {
    if (passwordStrength === 0) return 'bg-gray-300';
    if (passwordStrength === 1) return 'bg-red-500';
    if (passwordStrength === 2) return 'bg-yellow-500';
    if (passwordStrength === 3) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (passwordStrength === 0) return 'Too weak';
    if (passwordStrength === 1) return 'Weak';
    if (passwordStrength === 2) return 'Fair';
    if (passwordStrength === 3) return 'Good';
    return 'Strong';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
      <div className={step === 2 ? 'max-w-4xl w-full' : 'max-w-2xl w-full'}>
        {/* Logo */}
        <div className="text-center mb-6">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-44 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">{t('reg.title')}</h1>
          <p className="text-gray-600 mt-2" data-testid="signup-subhead">{theme.sub}</p>

          {/* HiBFF Privacy Promise — copy adapts to the persona */}
          <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl text-left">
            <p className="text-sm font-bold text-indigo-900 mb-1 flex items-center gap-1.5">
              <Shield size={16} className="text-indigo-600" /> {t('reg.privacyTitle')}
            </p>
            <p className="text-xs text-indigo-700 leading-relaxed">
              {persona === 'adult'
                ? t('reg.privacyAdult')
                : t('reg.privacyTeen')}
            </p>
          </div>
        </div>

        {/* Persona Selector — only on Step 1 (the email/Google entry step) */}
        {step === 1 && (
          <div
            className="grid grid-cols-3 gap-2 mb-6 p-1.5 rounded-2xl bg-white/70 backdrop-blur-sm border border-gray-200 shadow-sm"
            data-testid="signup-persona-selector"
            role="tablist"
          >
            {[
              { id: 'teen',   label: t('reg.teen'),   emoji: '\u{1F393}', age: '13\u201319' },
              { id: 'adult',  label: t('reg.adult'),  emoji: '\u{1F31F}', age: '20+' },
              { id: 'parent', label: t('reg.parent'), emoji: '\u{1F46A}', age: 'GUARDIAN'  },
            ].map((p) => {
              const active = p.id === persona && p.id !== 'parent';
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handlePersonaPick(p.id)}
                  data-testid={`signup-persona-pill-${p.id}`}
                  role="tab"
                  aria-selected={active}
                  className={`flex flex-col items-center justify-center gap-0.5 py-2.5 rounded-xl text-sm font-bold transition-all ${
                    active
                      ? `bg-gradient-to-r ${theme.grad} text-white shadow-md ${theme.glow}`
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="text-lg leading-none">{p.emoji}</span>
                  <span className="leading-none">{p.label}</span>
                  <span className={`text-[9px] font-semibold tracking-widest uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>
                    {p.age}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Registration Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Social Signup Buttons - Only show on Step 1 */}
          {step === 1 && (
            <>
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">
                  {t('reg.googleHint')}
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {/* Google Signup */}
                <button
                  type="button"
                  onClick={handleGoogleSignup}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('reg.googleBtn')}
                </button>

                {/* Native device-link wait state — retry/cancel (QA suggestion) */}
                {googleWaiting && (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-1.5" data-testid="google-signin-waiting-panel">
                    <p className="text-sm font-semibold text-blue-900">Finishing sign-in in your browser…</p>
                    <p className="text-xs text-blue-800">Approve HiBFF with Google in the browser that just opened, then come back here — we'll sign you in automatically.</p>
                    <button type="button" onClick={cancelGoogleWait} className="text-xs font-semibold underline text-blue-700" data-testid="google-signin-cancel-btn">
                      Cancel and try again
                    </button>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">{t('reg.orEmail')}</span>
                </div>
              </div>
            </>
          )}

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                1
              </div>
              <span className="text-sm font-medium hidden sm:inline">{t('reg.account')}</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300 mx-1"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                2
              </div>
              <span className="text-sm font-medium hidden sm:inline">{t('reg.aboutYou')}</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300 mx-1"></div>
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                3
              </div>
              <span className="text-sm font-medium hidden sm:inline">{age >= 13 && age <= 19 ? t('reg.parentInfo') : t('reg.finish')}</span>
            </div>
          </div>

          {error === 'HIBFF_AGE_BLOCKED' ? (
            <div id="register-error-box" className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3" data-testid="register-age-blocked">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">HiBFF teen accounts are for ages 13&ndash;19. If you're a parent or guardian, create a parent account to support your teen instead.</p>
              </div>
              <button type="button" onClick={() => navigate('/parent-register')} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-2.5 text-sm font-semibold" data-testid="register-parent-cta">
                Create a parent account
              </button>
              <button type="button" onClick={() => handlePersonaPick('adult')} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-lg py-2.5 text-sm font-semibold" data-testid="register-adult-cta">
                I'm 20 or over — create an adult account
              </button>
            </div>
          ) : error && (
            <div id="register-error-box" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="register-error-box">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Honeypot — invisible to humans, bots auto-fill it */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              onChange={(e) => { honeypotRef.current = e.target.value; }}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
            {step === 1 && (
              <div className="space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reg.fullName')}</label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reg.email')}</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('reg.password')}</label>
                  <p className="text-xs text-gray-500 mb-1.5" data-testid="register-password-rules">
                    At least 8 characters &mdash; add upper &amp; lower case, a number and a symbol for a stronger password.
                  </p>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-11 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('reg.phPw')}
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      data-testid="register-password-toggle-btn"
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-gray-400 hover:text-gray-600 min-w-[40px] min-h-[40px] flex items-center justify-center"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {/* Password Strength Meter */}
                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded ${passwordStrength >= level ? getStrengthColor() : 'bg-gray-200'}`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-600">{t('reg.pwStrength')} <span className="font-semibold">{getStrengthText()}</span></p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reg.confirmPassword')}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="w-full pl-10 pr-11 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('reg.phPw2')}
                      required
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(s => !s)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      data-testid="register-confirm-password-toggle-btn"
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-gray-400 hover:text-gray-600 min-w-[40px] min-h-[40px] flex items-center justify-center"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Date of Birth */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Calendar size={18} />
                    {t('reg.dob')}
                  </label>
                  <div className="grid grid-cols-[1.25fr_0.85fr_1fr] gap-2">
                    <select
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      className="w-full min-w-0 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="register-dob-month"
                    >
                      <option value="">{t('reg.month')}</option>
                      {months.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    <select
                      value={formData.day}
                      onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                      className="w-full min-w-0 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="register-dob-day"
                    >
                      <option value="">{t('reg.day')}</option>
                      {days.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <select
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className="w-full min-w-0 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="register-dob-year"
                    >
                      <option value="">{t('reg.year')}</option>
                      {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* School Enrollment Code (optional) — hidden for Adult persona */}
                {persona !== 'adult' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <School size={18} />
                    {t('reg.schoolCode')}
                    <span className="text-xs font-normal text-gray-400 ml-1">{t('reg.optional')}</span>
                  </label>
                  <input
                    type="text"
                    value={schoolCode}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setSchoolCode(val);
                      if (!val.trim()) {
                        setSchoolValidation({ status: 'idle', name: '', error: '' });
                      }
                    }}
                    onBlur={() => validateSchoolCode(schoolCode)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono tracking-wider ${
                      schoolValidation.status === 'valid' ? 'border-green-400 bg-green-50' :
                      schoolValidation.status === 'invalid' ? 'border-red-300 bg-red-50' :
                      'border-gray-300'
                    }`}
                    placeholder="e.g. AB12CD34"
                    maxLength={12}
                    data-testid="school-code-input"
                  />
                  {validatingCode && (
                    <p className="text-xs text-gray-500 mt-1">{t('reg.checking')}</p>
                  )}
                  {schoolValidation.status === 'valid' && (
                    <div className="flex items-center gap-1.5 mt-1.5" data-testid="school-code-valid">
                      <CheckCircle size={14} className="text-green-600" />
                      <span className="text-xs font-medium text-green-700">
                        Linked to {schoolValidation.name}
                      </span>
                    </div>
                  )}
                  {schoolValidation.status === 'invalid' && (
                    <div className="flex items-center gap-1.5 mt-1.5" data-testid="school-code-invalid">
                      <AlertCircle size={14} className="text-red-500" />
                      <span className="text-xs text-red-600">{schoolValidation.error}</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {t('reg.schoolHint')}
                  </p>
                </div>
                )}

                {/* Mobile Number (MANDATORY) — country code + national number */}
                <div data-testid="register-phone-block">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Phone size={18} />
                    {t('reg.mobile')}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={formData.phoneCountry}
                      onChange={(e) => setFormData({ ...formData, phoneCountry: e.target.value })}
                      className="py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm w-32"
                      data-testid="register-phone-country-select"
                      aria-label="Country code"
                      required
                    >
                      <option value="+1">🇺🇸 +1 (US)</option>
                      <option value="+1-ca">🇨🇦 +1 (CA)</option>
                      <option value="+44">🇬🇧 +44 (UK)</option>
                      <option value="+61">🇦🇺 +61 (AU)</option>
                      <option value="+64">🇳🇿 +64 (NZ)</option>
                      <option value="+353">🇮🇪 +353 (IE)</option>
                      <option value="+91">🇮🇳 +91 (IN)</option>
                      <option value="+27">🇿🇦 +27 (ZA)</option>
                      <option value="+33">🇫🇷 +33 (FR)</option>
                      <option value="+49">🇩🇪 +49 (DE)</option>
                      <option value="+34">🇪🇸 +34 (ES)</option>
                      <option value="+39">🇮🇹 +39 (IT)</option>
                      <option value="+31">🇳🇱 +31 (NL)</option>
                      <option value="+46">🇸🇪 +46 (SE)</option>
                      <option value="+47">🇳🇴 +47 (NO)</option>
                      <option value="+45">🇩🇰 +45 (DK)</option>
                      <option value="+41">🇨🇭 +41 (CH)</option>
                      <option value="+43">🇦🇹 +43 (AT)</option>
                      <option value="+32">🇧🇪 +32 (BE)</option>
                      <option value="+351">🇵🇹 +351 (PT)</option>
                      <option value="+65">🇸🇬 +65 (SG)</option>
                      <option value="+852">🇭🇰 +852 (HK)</option>
                      <option value="+81">🇯🇵 +81 (JP)</option>
                      <option value="+82">🇰🇷 +82 (KR)</option>
                      <option value="+86">🇨🇳 +86 (CN)</option>
                      <option value="+971">🇦🇪 +971 (AE)</option>
                      <option value="+966">🇸🇦 +966 (SA)</option>
                      <option value="+972">🇮🇱 +972 (IL)</option>
                      <option value="+55">🇧🇷 +55 (BR)</option>
                      <option value="+52">🇲🇽 +52 (MX)</option>
                      <option value="+54">🇦🇷 +54 (AR)</option>
                    </select>
                    <div className="relative flex-1">
                      <Phone size={18} className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9\s()+-]/g, '') })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="555 123 4567"
                        required
                        inputMode="tel"
                        autoComplete="tel-national"
                        data-testid="register-phone-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Shield size={12} />
                    {t('reg.mobileNote')}
                  </p>
                </div>

                {/* SMS consent — required, unchecked by default (A2P 10DLC web-form opt-in) */}
                <label className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer" data-testid="register-sms-consent-block">
                  <input
                    type="checkbox"
                    checked={formData.smsConsent}
                    onChange={(e) => setFormData({ ...formData, smsConsent: e.target.checked })}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="register-sms-consent-checkbox"
                    required
                  />
                  <span>
                    <span className="font-semibold text-gray-800">Required<span className="text-red-500">*</span> — </span>
                    By checking this box, I agree to receive transactional SMS updates and account notifications from HiBFF. Message and data rates may apply. Message frequency varies. Reply STOP to cancel or HELP for help. View our{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-700">Terms of Service</a> and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-blue-700">Privacy Policy</a>.
                  </span>
                </label>

                {/* Parent / Guardian Details — MANDATORY for the teen persona */}
                {persona === 'teen' && (
                  <div
                    className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/60 p-5 space-y-4"
                    data-testid="teen-parent-block"
                  >
                    <div className="flex items-start gap-2">
                      <Shield size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-indigo-900 text-sm">{t('reg.parentSection')}</p>
                        <p className="text-xs text-indigo-700 mt-0.5">
                          {t('reg.parentSectionNote')}
                        </p>
                      </div>
                    </div>

                    {/* 18-19 explainer (QA suggestion) */}
                    {(declaredAge === 18 || declaredAge === 19) && (
                      <div className="p-3 rounded-xl bg-white border border-indigo-200" data-testid="adult-teen-explainer">
                        <p className="text-xs text-indigo-900 leading-relaxed">
                          <strong>Aged 18 or 19?</strong> You're still part of a family on HiBFF &mdash; enter your parent or guardian's details here. They approve your account and look after any billing, while your conversations stay private to you.
                        </p>
                      </div>
                    )}

                    {/* Parent full name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t('reg.parentName')} <span className="text-red-600">*</span>
                      </label>
                      <div className="relative">
                        <User size={18} className="absolute left-3 top-3 text-gray-400" />
                        <input
                          type="text"
                          value={formData.parentName}
                          onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                          placeholder="e.g. Sarah Parsons"
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                          required
                          data-testid="register-parent-name-input"
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    {/* Parent email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t('reg.parentEmail')} <span className="text-red-600">*</span>
                      </label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-3 top-3 text-gray-400" />
                        <input
                          type="email"
                          value={formData.parentEmail}
                          onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                          placeholder="parent@example.com"
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                          required
                          data-testid="register-parent-email-input"
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    {/* Parent mobile (same country code as teen) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t('reg.parentPhone')} <span className="text-red-600">*</span>
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={formData.phoneCountry}
                          onChange={(e) => setFormData({ ...formData, phoneCountry: e.target.value })}
                          className="py-2 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-xs w-[7.25rem] shrink-0"
                          aria-label="Parent country code"
                          data-testid="register-parent-phone-country"
                        >
                          <option value="+1">🇺🇸 +1 (US)</option>
                          <option value="+1-ca">🇨🇦 +1 (CA)</option>
                          <option value="+44">🇬🇧 +44 (UK)</option>
                          <option value="+61">🇦🇺 +61 (AU)</option>
                          <option value="+64">🇳🇿 +64 (NZ)</option>
                          <option value="+353">🇮🇪 +353 (IE)</option>
                          <option value="+91">🇮🇳 +91 (IN)</option>
                          <option value="+27">🇿🇦 +27 (ZA)</option>
                          <option value="+33">🇫🇷 +33 (FR)</option>
                          <option value="+49">🇩🇪 +49 (DE)</option>
                          <option value="+34">🇪🇸 +34 (ES)</option>
                          <option value="+39">🇮🇹 +39 (IT)</option>
                          <option value="+31">🇳🇱 +31 (NL)</option>
                          <option value="+46">🇸🇪 +46 (SE)</option>
                          <option value="+47">🇳🇴 +47 (NO)</option>
                          <option value="+45">🇩🇰 +45 (DK)</option>
                          <option value="+41">🇨🇭 +41 (CH)</option>
                          <option value="+43">🇦🇹 +43 (AT)</option>
                          <option value="+32">🇧🇪 +32 (BE)</option>
                          <option value="+351">🇵🇹 +351 (PT)</option>
                          <option value="+65">🇸🇬 +65 (SG)</option>
                          <option value="+852">🇭🇰 +852 (HK)</option>
                          <option value="+81">🇯🇵 +81 (JP)</option>
                          <option value="+82">🇰🇷 +82 (KR)</option>
                          <option value="+86">🇨🇳 +86 (CN)</option>
                          <option value="+971">🇦🇪 +971 (AE)</option>
                          <option value="+966">🇸🇦 +966 (SA)</option>
                          <option value="+972">🇮🇱 +972 (IL)</option>
                          <option value="+55">🇧🇷 +55 (BR)</option>
                          <option value="+52">🇲🇽 +52 (MX)</option>
                          <option value="+54">🇦🇷 +54 (AR)</option>
                        </select>
                        <div className="relative flex-1 min-w-0">
                          <Phone size={18} className="absolute left-3 top-3 text-gray-400" />
                          <input
                            type="tel"
                            value={formData.parentPhone}
                            onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value.replace(/[^0-9\s()+-]/g, '') })}
                            placeholder="555 987 6543"
                            className="w-full pl-9 pr-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-xs"
                            required
                            inputMode="tel"
                            autoComplete="off"
                            data-testid="register-parent-phone-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Billing notice */}
                    <div
                      className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200"
                      data-testid="parent-billing-notice"
                    >
                      <span className="text-lg leading-none mt-0.5" aria-hidden>💳</span>
                      <p className="text-xs text-amber-900 leading-relaxed">
                        <strong>{t('reg.headsUpTitle')}</strong> {t('reg.headsUp')}
                      </p>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleContinue}
                  className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
                  data-testid="register-continue-step1"
                >
                  {t('reg.continue')}
                </button>
              </div>
            )}

            {step === 2 && (
              <div data-testid="registration-profiling-step">
                <MayaErrorBoundary>
                  <MayaProfileChat
                    formData={formData}
                    onAnswer={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                    onComplete={() => setStep(3)}
                    onBack={() => setStep(1)}
                  />
                </MayaErrorBoundary>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                {age >= 13 && age <= 19 ? (
                  <>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-900 font-semibold mb-1">{t('reg.consentTitle')}</p>
                      <p className="text-sm text-blue-700">
                        {t('reg.consentSub')}
                      </p>
                    </div>

                    {/* Sprint 208 Master item #8 — explicit pre-notice so the
                        teen expects the parent email/SMS + Charter return. */}
                    <div className="p-4 bg-gradient-to-br from-amber-50 to-pink-50 border border-amber-200 rounded-lg" data-testid="teen-parent-notice">
                      <p className="text-sm text-amber-900 font-semibold mb-1">{t('reg.nextTitle')}</p>
                      <p className="text-sm text-amber-800 leading-relaxed">
                        {t('reg.nextBody')}
                      </p>
                    </div>

                    {/* Parent Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('reg.parentName')} <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={formData.parentName}
                        onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="First and last name"
                        data-testid="register-parent-name"
                        required
                      />
                    </div>

                    {/* Parent Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('reg.parentEmail')} <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-3 top-3 text-gray-400" />
                        <input
                          type="email"
                          value={formData.parentEmail}
                          onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="parent@email.com"
                          data-testid="register-parent-email"
                          required
                        />
                      </div>
                    </div>

                    {/* Parent Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('reg.parentPhone')} <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Phone size={18} className="absolute left-3 top-3 text-gray-400" />
                        <input
                          type="tel"
                          value={formData.parentPhone}
                          onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value.replace(/[^0-9\s()+-]/g, '') })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 placeholder:text-sm"
                          placeholder="555 123 4567"
                          data-testid="register-parent-phone"
                          required
                        />
                      </div>
                    </div>

                    {/* Relationship */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('reg.relationship')}</label>
                      <div className="relative">
                        <Users size={18} className="absolute left-3 top-3 text-gray-400" />
                        <select
                          value={formData.relationship}
                          onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="parent">{t('reg.relParent')}</option>
                          <option value="guardian">{t('reg.relGuardian')}</option>
                          <option value="other">{t('reg.relOther')}</option>
                        </select>
                      </div>
                    </div>

                    {/* Parent Consent Checkbox */}
                    <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.parentConsent}
                        onChange={(e) => setFormData({ ...formData, parentConsent: e.target.checked })}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        required
                      />
                      <label className="text-sm text-gray-700">
                        <strong>{t('reg.permStrong')}</strong> {t('reg.permRest')}
                      </label>
                    </div>
                  </>
                ) : (
                  <div className="space-y-6">
                    <div className="p-8 bg-green-50 border border-green-200 rounded-2xl text-center">
                      <img src="/hibff-logo.png" alt="HiBFF" className="mx-auto mb-5" style={{ height: 96, width: 'auto' }} />
                      <h3 className="text-2xl font-black text-green-900 mb-2">{t('reg.allSet')}</h3>
                      <p className="text-base text-green-700">{t('reg.allSetSub')}</p>
                    </div>

                    <div className="p-5 bg-purple-50 border border-purple-200 rounded-xl">
                      <p className="text-sm text-purple-900">
                        <strong>{t('reg.freeStrong')}</strong> {t('reg.freeRest')}
                      </p>
                    </div>

                    {/* Remember Me */}
                    <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.rememberMe}
                        onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label className="text-sm text-gray-700">
                        <strong>{t('reg.rememberStrong')}</strong> {t('reg.rememberRest')}
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                    data-testid="step3-back-btn"
                  >
                    {t('reg.back')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
                    data-testid="create-account-btn"
                  >
                    {t('reg.createBtn')}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('reg.haveAccount')}{' '}
              <a href="/login" className="font-medium text-blue-600 hover:text-blue-700">
                {t('reg.login')}
              </a>
            </p>
          </div>
        </div>

        {/* Date of Birth Modal (for social signup) */}
        <DateOfBirthModal
          isOpen={showDOBModal}
          onClose={() => {
            setShowDOBModal(false);
            setPendingSocialUser(null);
          }}
          onSubmit={handleDOBSubmit}
          userName={pendingSocialUser?.name || 'there'}
        />
      </div>
    </div>
  );
};

export default Register;
